import { Button, Text } from '@components/ui';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TextValidator } from 'react-material-ui-form-validator';
import PlacesAutocomplete from 'react-places-autocomplete';

interface UpdateHireAddressFieldsProps {
  r8tedVehicleAddress: string;
  handleChangeAddress: (address: string) => void;
  handleSelectAddress: (address: string) => void;
  zipCode: string | undefined;
  handleChangeObject: (event: ChangeEvent<HTMLInputElement>) => void;
  checked: boolean;
  handleCheckBoxChange: () => void;
}

export function UpdateHireAddressFields({
  r8tedVehicleAddress,
  handleChangeAddress,
  handleSelectAddress,
  zipCode,
  handleChangeObject,
  checked,
  handleCheckBoxChange,
}: UpdateHireAddressFieldsProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <Text
        variant="label"
        sx={{
          mt: 1.25,
          mb: 1.25,
          display: 'block',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {t('hire.addressDetails')}
      </Text>
      <Grid
        container
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        spacing={2}
        style={{ display: 'flex' }}
      >
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: 50,
              borderRadius: '5px',
            }}
          >
            <PlacesAutocomplete
              value={r8tedVehicleAddress}
              onChange={handleChangeAddress}
              onSelect={handleSelectAddress}
            >
              {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
              }) => (
                <div style={{ width: '100%' }}>
                  <input
                    {...getInputProps({
                      placeholder: t(
                        'hire.updateHire.vehicleAddressPlaceholder'
                      ),
                      className: 'location-search-input input_address_details',
                    })}
                    style={{
                      width: '100%',
                      height: '50px',
                      borderRadius: '5px',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 'calc(100% + 4px)',
                      zIndex: 50,
                      maxHeight: 240,
                      overflowY: 'auto',
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    {loading ? <div>{t('common.loading')}</div> : null}
                    {suggestions.map((suggestion, suggestionIndex) => {
                      const className = suggestion.active
                        ? 'suggestion-item--active'
                        : 'suggestion-item';
                      const style = {
                        backgroundColor: 'rgb(var(--ink))',
                        cursor: 'pointer' as const,
                      };
                      return (
                        <div
                          key={suggestionIndex}
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      );
                    })}
                  </Box>
                </div>
              )}
            </PlacesAutocomplete>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <TextValidator
            sx={{
              width: '100%',
              borderRadius: '5px',
              backgroundColor: 'white',
              '& fieldset': { border: 'none' },
            }}
            label={t('hire.updateHire.zipCode')}
            onChange={(event) => handleChangeObject(event)}
            type="number"
            name="zipCode"
            value={zipCode}
          />
        </Grid>

        <div
          style={{
            marginTop: '15px',
            padding: '5px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              sx={{
                padding: 'none',
                marginLeft: '0px',
                marginRight: '0px',
              }}
              label={null}
              value={checked}
              control={
                <Checkbox onChange={handleCheckBoxChange} size="small" />
              }
            />
            <span style={{ fontSize: '14px' }}>
              {t('hire.updateHire.agreeTo')}&nbsp;
              <Box
                component="span"
                sx={{
                  textDecoration: 'underline',
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                {t('hire.terms')}
              </Box>
            </span>
          </span>
          <span style={{ width: '40%' }}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              sx={{
                borderRadius: '0px 15px 0px 15px',
                height: 40,
              }}
            >
              {t('common.next')}
            </Button>
          </span>
        </div>
      </Grid>
    </>
  );
}
